import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { PoBreadcrumb, PoNotificationService } from '@po-ui/ng-components';
import { PendenciaEnqueueResponse, RpaService } from '../../../core/rpa/rpa.service';
import { Observable } from 'rxjs';
import {
  RpaProcessamento,
  RpaProcessamentoEmpresa,
  RpaProcessamentoService,
  RpaProcessamentoStatus
} from '../../../core/rpa/rpa-processamento.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-rpas-relatorio-pendencias',
  templateUrl: './rpas-relatorio-pendencias.component.html',
  styleUrls: ['./rpas-relatorio-pendencias.component.css'],
  standalone: false
})
export class RPAsRelatorioPendenciasComponent implements OnInit, OnDestroy {
  tipoDivida: 'inscrita' | 'nao-inscrita' = 'nao-inscrita';
  codigoEmpresa = '';
  arquivoNome = '';
  arquivoPlanilha: File | null = null;
  loading = false;
  loadingArquivo = false;
  sincronizandoArquivos = false;
  showProgress = false;
  processamentoAtual: RpaProcessamento | null = null;
  private processamentoSub?: Subscription;
  private processamentoAtualId = '';

  readonly tipoDividaOptions = [
    { label: 'Dívida Estadual Inscrita', value: 'inscrita' },
    { label: 'Dívida Estadual Não Inscrita', value: 'nao-inscrita' }
  ];

  readonly breadcrumb: PoBreadcrumb = {
    items: [
      { label: 'Home', link: '/home' },
      { label: 'Pendência estadual', link: '/relatorio-pendencias' },
      { label: 'Consulta de pendência' }
    ]
  };

  constructor(
    private readonly router: Router,
    private readonly rpaService: RpaService,
    private readonly rpaProcessamentoService: RpaProcessamentoService,
    private readonly poNotificationService: PoNotificationService
  ) {}

  voltarPendenciaEstadual(): void {
    this.router.navigate(['relatorio-pendencias']);
  }

  ngOnInit(): void {
    this.processamentoSub = this.rpaProcessamentoService.processamentos$.subscribe((lista) => {
      if (!this.processamentoAtualId) return;
      this.processamentoAtual = lista.find((p) => p.id === this.processamentoAtualId) ?? null;
    });
  }

  ngOnDestroy(): void {
    this.processamentoSub?.unsubscribe();
  }

  get inicioProcessamento(): Date | null {
    return this.processamentoAtual?.inicio ? new Date(this.processamentoAtual.inicio) : null;
  }

  get fimProcessamento(): Date | null {
    return this.processamentoAtual?.fim ? new Date(this.processamentoAtual.fim) : null;
  }

  get processoStatus(): RpaProcessamentoStatus {
    return this.processamentoAtual?.status ?? 'PROCESSANDO';
  }

  get processoMensagem(): string {
    return this.processamentoAtual?.mensagem ?? '';
  }

  gerarRelatorioPendencias() {
    if (this.loading) return;

    const codigoDigitado = this.codigoEmpresa.trim();

    this.showProgress = true;
    this.processamentoAtual = null;
    this.processamentoAtualId = '';
    this.poNotificationService.information(
      'Solicitação enviada. O andamento será exibido nesta tela e também em RPA.'
    );

    this.loading = true;
    const tipoApi = this.tipoDivida === 'nao-inscrita' ? 'naoInscrita' : 'inscrita';

    let request$: Observable<PendenciaEnqueueResponse> | null = this.arquivoPlanilha
      ? this.rpaService.requestPendenciaCnpjsPorPlanilha(tipoApi, this.arquivoPlanilha)
      : null;

    if (!request$ && codigoDigitado) {
      const codigoNumerico = codigoDigitado.replace(/\D/g, '');
      if (!codigoNumerico) {
        this.loading = false;
        this.showProgress = false;
        this.poNotificationService.warning('Informe um código de empresa numérico válido.');
        return;
      }
      request$ =
        this.tipoDivida === 'nao-inscrita'
          ? this.rpaService.requestPendenciaEstadualNaoInscritaPorCodigo(codigoNumerico)
          : this.rpaService.requestPendenciaEstadualPorCodigo(codigoNumerico);
    }

    if (!request$) {
      this.loading = false;
      this.showProgress = false;
      this.poNotificationService.warning('Informe um código de empresa ou envie uma planilha com a coluna CNPJ.');
      return;
    }

    request$.subscribe({
      next: (response: any) => {
        this.loading = false;
        if (response?.error) {
          this.processamentoAtual = {
            id: '',
            tipo: this.tipoDivida === 'nao-inscrita' ? 'pendencia-nao-inscrita' : 'pendencia-inscrita',
            titulo: this.getTipoLabel(),
            status: 'BLOQUEADO',
            mensagem: response.error,
            inicio: new Date().toISOString(),
            fim: new Date().toISOString(),
            tipoDivida: this.tipoDivida,
            codigosRastreio: [],
            totalEmpresas: 0,
            empresasConcluidas: 0,
            empresas: []
          };
          this.poNotificationService.error(response.error);
          return;
        }

        const processamento = this.rpaProcessamentoService.registrarPendencia(
          this.tipoDivida,
          response,
          codigoDigitado
        );
        this.processamentoAtualId = processamento.id;
        this.processamentoAtual = processamento;
        this.poNotificationService.success(
          `Solicitação de ${this.getTipoLabel().toLowerCase()} enviada. Acompanhe em RPA.`
        );
      },
      error: (err: any) => {
        this.loading = false;
        this.processamentoAtual = {
          id: '',
          tipo: this.tipoDivida === 'nao-inscrita' ? 'pendencia-nao-inscrita' : 'pendencia-inscrita',
          titulo: this.getTipoLabel(),
          status: 'BLOQUEADO',
          mensagem: err?.error?.error || err?.error?.message || 'Processo bloqueado pelo servidor.',
          inicio: new Date().toISOString(),
          fim: new Date().toISOString(),
          tipoDivida: this.tipoDivida,
          codigosRastreio: [],
          totalEmpresas: 0,
          empresasConcluidas: 0,
          empresas: []
        };
        this.poNotificationService.error(`Erro ao gerar relatório de ${this.getTipoLabel().toLowerCase()}.`);
      }
    });
  }

  onChangeTipoDivida(value: 'inscrita' | 'nao-inscrita') {
    this.tipoDivida = value;
  }

  onArquivoSelecionado(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.processarArquivo(file);
  }

  onDropArquivo(event: DragEvent): void {
    event.preventDefault();
    const file = event.dataTransfer?.files?.[0];
    if (!file) return;
    this.processarArquivo(file);
  }

  onDragOverArquivo(event: DragEvent): void {
    event.preventDefault();
  }

  limparArquivo(): void {
    this.arquivoNome = '';
    this.arquivoPlanilha = null;
  }

  getTipoLabel(): string {
    return this.tipoDivida === 'nao-inscrita'
      ? 'Dívida Estadual Não Inscrita'
      : 'Dívida Estadual Inscrita';
  }

  getStatusLabel(): string {
    if (this.processoStatus === 'PRONTO') return 'Pronto';
    if (this.processoStatus === 'BLOQUEADO') return 'Bloqueado';
    return 'Processando';
  }

  podeVisualizarArquivoEmpresa(empresa: RpaProcessamentoEmpresa): boolean {
    return empresa.status === 'PRONTO' && !!String(empresa.fileUrl || '').trim();
  }

  sincronizarArquivosRpa(): void {
    if (this.sincronizandoArquivos) return;

    const botId = String(this.processamentoAtual?.botId ?? '').trim();

    if (!botId || !this.processamentoAtualId) {
      this.poNotificationService.warning(
        'Não foi possível localizar os dados desta consulta. Envie a planilha novamente.'
      );
      return;
    }

    this.sincronizandoArquivos = true;
    const since = this.processamentoAtual?.inicio;
    this.rpaService.requestPendenciaOutboxStatus(botId, since).subscribe({
      next: (outboxResponse) => {
        this.rpaProcessamentoService.atualizarComOutboxStatus(
          this.processamentoAtualId,
          outboxResponse
        );
        this.atualizarProcessamentoAtual();
        this.sincronizandoArquivos = false;
        this.poNotificationService.success('Status atualizado.');
      },
      error: (err: any) => {
        this.sincronizandoArquivos = false;
        const detalhe =
          err?.error?.error ||
          err?.error?.message ||
          err?.error?.detailedMessage ||
          err?.message;
        this.poNotificationService.error(
          detalhe
            ? `Não foi possível atualizar o status: ${detalhe}`
            : 'Não foi possível atualizar o status. Tente novamente em instantes.'
        );
      }
    });
  }

  visualizarArquivoEmpresa(empresa: RpaProcessamentoEmpresa): void {
    const objectNameAtual = String(empresa.fileUrl || '').trim();
    if (objectNameAtual) {
      this.abrirArquivoBucket(objectNameAtual);
      return;
    }

    if (this.sincronizandoArquivos) return;
    this.sincronizandoArquivos = true;
    this.rpaService.requestRpaFiles().subscribe({
      next: (response: any) => {
        this.rpaProcessamentoService.atualizarComArquivos(response);
        this.atualizarProcessamentoAtual();
        this.sincronizandoArquivos = false;
        const emp = this.processamentoAtual?.empresas.find((e) => e.codigo === empresa.codigo);
        const objectName = String(emp?.fileUrl || '').trim();
        if (!objectName) {
          this.poNotificationService.warning(
            'Arquivo ainda não disponível. Aguarde o processamento e clique em "Atualizar status".'
          );
          return;
        }
        this.abrirArquivoBucket(objectName);
      },
      error: () => {
        this.sincronizandoArquivos = false;
        this.poNotificationService.error('Erro ao buscar arquivos RPA.');
      }
    });
  }

  private abrirArquivoBucket(objectName: string): void {
    this.loadingArquivo = true;
    this.rpaService.requestRpaUrlFile(objectName).subscribe({
      next: (response: any) => {
        this.loadingArquivo = false;
        const url = this.rpaService.extrairUrlAssinadaResposta(response);
        if (!url) {
          this.poNotificationService.error('A API não retornou a URL do arquivo.');
          return;
        }
        window.open(url, '_blank');
      },
      error: () => {
        this.loadingArquivo = false;
        this.poNotificationService.error('Erro ao carregar o arquivo.');
      }
    });
  }

  private atualizarProcessamentoAtual(): void {
    if (!this.processamentoAtualId) return;
    this.processamentoAtual =
      this.rpaProcessamentoService.getById(this.processamentoAtualId) ?? null;
  }

  private processarArquivo(file: File): void {
    const extensaoValida = /\.(csv|xlsx|xlsm|xls)$/i.test(file.name);
    if (!extensaoValida) {
      this.poNotificationService.warning('Envie uma planilha .csv, .xlsx ou .xlsm.');
      return;
    }

    this.arquivoPlanilha = file;
    this.arquivoNome = file.name;
    this.codigoEmpresa = '';
    this.poNotificationService.success('Planilha carregada. Clique em "Gerar Relatório" para enviar ao servidor.');
  }
}
